import { useState, useCallback } from "react"
import { JobListResponse, JobStatusResponse } from "types/automation.types"
import {
  useJobsQuery,
  useJobStatusQuery,
} from "features/Automation/api/useAutomationQuery"
import { isJobCompleted } from "utils/automationUtils"

/**
 * Job polling state for the Automation view.
 *
 * Built on react-query's refetchInterval: the jobs list polls every 2s while
 * `isPolling`, and the selected job's status polls every second until the
 * job completes (the interval then stops on its own; the final status stays
 * visible). Unmounting the consumer stops all polling automatically.
 */
export const useJobPolling = () => {
  const [isPolling, setIsPolling] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)

  const jobsQuery = useJobsQuery(isPolling)
  const jobStatusQuery = useJobStatusQuery(selectedJobId)

  const jobs: JobListResponse[] = jobsQuery.data ?? []
  const jobStatusData: JobStatusResponse | null = selectedJobId
    ? jobStatusQuery.data ?? null
    : null

  // "Polling" from the consumer's perspective: a job is selected and hasn't
  // reported a completed state yet.
  const isJobStatusPolling =
    Boolean(selectedJobId) &&
    (!jobStatusData || !isJobCompleted(jobStatusData.status))

  const startPolling = useCallback(() => setIsPolling(true), [])
  const stopPolling = useCallback(() => setIsPolling(false), [])

  /** Toggle job selection for console viewing. */
  const handleJobSelection = useCallback((jobId: string | null) => {
    setSelectedJobId((prev) => (prev === jobId ? null : jobId))
  }, [])

  /** One-time jobs fetch outside the polling loop (works while disabled). */
  const { refetch: refetchJobs } = jobsQuery
  const fetchJobs = useCallback(async () => {
    await refetchJobs()
  }, [refetchJobs])

  /** Stop all polling (kept for the existing consumer's unmount cleanup). */
  const cleanup = useCallback(() => {
    setIsPolling(false)
    setSelectedJobId(null)
  }, [])

  return {
    // State
    jobs,
    isPolling,
    selectedJobId,
    jobStatusData,
    isJobStatusPolling,

    // Actions
    startPolling,
    stopPolling,
    handleJobSelection,
    fetchJobs,
    cleanup,
  }
}
